import { ErrorMessage } from '@hookform/error-message';
import {
  ChangeEvent,
  ComponentProps, ReactElement,
} from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import {
  FormFeedback,
  FormGroup,
  Input,
  InputGroup,
  InputGroupText,
  Label,
} from 'reactstrap';

interface FieldValueType {
  name: string;
  type: ComponentProps<typeof Input>['type'];
  placeholder?: string;
  defaultValue?: string;
  hideFormError?: boolean;
  id?: string;
  value?: string;
  className?: string;
  checked?: boolean;
  disabled?: boolean;
  autoComplete?: string;
  label?: string;
  size?: string;
  inputGroupItems?: React.ReactNode;
  formGroupClass?: string;
  onClick?: () => void;
  mandatory?: boolean;
  readOnly?: boolean;
  customHandleChange?: (
    inputEvent: ChangeEvent<HTMLInputElement>
  ) => void
}

interface RenderInputType {
  inputGroupItems: React.ReactNode;
  children: ReactElement<Input>;
  errorMessageComponent: () => React.ReactNode;
}

const RenderInput = (props: RenderInputType) => {
  const {
    inputGroupItems,
    children,
    errorMessageComponent,
  } = props;
  if (inputGroupItems) {
    return (
      <InputGroup className="ls-input-group">
        {children}
        <InputGroupText>
          {inputGroupItems}
        </InputGroupText>
        {errorMessageComponent()}
      </InputGroup>
    );
  }
  if (children) {
    return (
      <>
        {children}
        {errorMessageComponent()}
      </>
    );
  }
  return <div />;
};

const InputField = (props: FieldValueType) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const {
    name,
    type,
    placeholder = '',
    hideFormError = false,
    value = undefined,
    checked = false,
    label = '',
    inputGroupItems = undefined,
    formGroupClass = '',
    mandatory = false,
    size = '',
    className = '',
    readOnly = false,
    customHandleChange = null,
    ...inputProps
  } = props;

  const errorMessageComponent = () => {
    if (!hideFormError) {
      return (
        <ErrorMessage
          errors={errors}
          name={name}
          render={({ message }) => <FormFeedback>{message}</FormFeedback>}
        />
      );
    }
    return <div />;
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <FormGroup className={`ls-form-group ${size || ''} ${formGroupClass || ''}`}>
          {label ? (
            <Label for="">
              {label}
              {' '}
              {mandatory ? <span>*</span> : ''}
            </Label>
          ) : (
            ''
          )}
          <RenderInput
            inputGroupItems={inputGroupItems}
            errorMessageComponent={errorMessageComponent}
          >
            <Input
              {...field}
              type={type}
              placeholder={placeholder}
              value={value || field?.value}
              checked={checked}
              {...inputProps}
              className={`${className ?? ''} ${
                !inputGroupItems || type === 'password' ? size : ''
              }`}
              onChange={(inputChangeEvent) => {
                if (customHandleChange) {
                  field.onChange(customHandleChange(inputChangeEvent));
                } else {
                  field.onChange(inputChangeEvent);
                }
              }}
              // invalid={!!get(errors, name)}
              readOnly={readOnly || false}
            />
          </RenderInput>
        </FormGroup>
      )}
    />
  );
};

export default InputField;
